/**
 * 槽位框 —— 一个槽 = 一张将来会有的图(W2,2026-08-02)。
 *
 * ═══ 两条尺寸纪律 ═════════════════════════════════════════════════════════════
 * ① **定高、宽按比例算**(`w = stageH × 宽高比`),不是「定宽让高度飘」。
 *    定宽写法下 9:16 在 220px 宽的列里会长到 390px 高,同一行里 9:16 和 16:9 差三倍高,
 *    底下的标题行全部对不齐(设计台截图上一眼看穿)。定高之后一排卡高度齐,
 *    而**宽度的差异正好就是画幅的差异** —— 比例信息不用再靠文字说。
 * ② 四态共用同一个盒子:出图只是把盒子里的内容换掉,盒子本身不动。
 *    这是「整个等待过程网格零重排」的实现基础。
 *
 * ═══ 动效 ═════════════════════════════════════════════════════════════════════
 * `waiting` 用 `.fx-slot-wait`(同相位微光,读作「这一片还没回来」),
 * `landed` 的 <img> 用 `.fx-slot-land`(挂载即跑一次的落位)。两者都只动 opacity/transform,
 * 且在 `prefers-reduced-motion: reduce` 下静止(见 index.css)。
 *
 * ★这里**没有**「正在画这一张」的态:并行 fan-out 时同时在画好几张,后端也不告诉前端
 *   哪张在机器上 —— 指名一张等于说了一句我们不知道的话。
 */
import { AlertCircle } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import type { GenSlot } from "../../../model/generationProgress";

/** "9:16" → 1.78;非法值回落 0.75(3:4)。 */
export function slotAspect(ratio: string): number {
  const [w, h] = ratio.split(":").map(Number);
  return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? w / h : 0.75;
}

/** 一组槽里最宽的画幅 —— 网格列宽按它算,免得全竖图被 16:9 的列宽撑出一片空地。 */
export function maxSlotAspect(slots: Pick<GenSlot, "ratio">[]): number {
  return slots.reduce((m, s) => Math.max(m, slotAspect(s.ratio)), 0.5);
}

export function SlotFrame({
  slot,
  stageH,
  dense = false,
  className,
}: {
  slot: GenSlot;
  /** 图区高度(px)。同一处调用点内所有槽用同一个值 → 行高一致。 */
  stageH: number;
  /** 紧凑档:不写比例文字、失败不写说明(缩略图尺寸下放不下) */
  dense?: boolean;
  className?: string;
}) {
  const url = slot.asset?.thumbUrl ?? slot.asset?.baseUrl ?? null;
  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden rounded-md",
        slot.state === "landed" && "bg-muted",
        // 待续出:比 waiting 更静 —— 它不在出,不该动(动 = 暗示「正在画」,说谎)
        slot.state === "waiting" &&
          slot.deferred &&
          "border border-dashed border-border/60 bg-muted/20 opacity-60",
        slot.state === "waiting" &&
          !slot.deferred &&
          "fx-slot-wait border border-dashed border-border bg-muted/40",
        slot.state === "failed" && "border border-destructive/50 bg-destructive/[0.06]",
        className
      )}
      /**
       * ★宽度 `min(stageH×比例, 100%)` + `aspect-ratio`,不是写死 height/width。
       *   宽屏下算出来正好是 stageH 高(一排高度齐);390px 窄屏下 16:9 的槽本来要 370px,
       *   写死就会把整页顶出横向滚动条(e2e 实测三个断点里就 mobile 红)。
       *   夹到容器宽之后比例仍由 aspect-ratio 保住,只是整体变小。
       */
      style={{
        width: `min(${Math.round(stageH * slotAspect(slot.ratio))}px, 100%)`,
        aspectRatio: slot.ratio.replace(":", " / "),
      }}
    >
      {slot.state === "landed" && url && (
        /* F-04 同族:盒子按**计划**画幅撑,引擎实际出图比例偏了时 cover 会裁掉
           顶部标题区 —— contain 宁可留边也不裁内容(下载是完整的,预览不该说谎)。 */
        <img
          src={url}
          alt={`seq ${slot.seq}`}
          className="fx-slot-land h-full w-full object-contain"
          loading="lazy"
          decoding="async"
        />
      )}

      {slot.state === "waiting" && !dense && (
        <span className="absolute inset-0 flex items-center justify-center text-2xs text-muted-foreground/70">
          {slot.deferred ? "待续出" : slot.ratio}
        </span>
      )}

      {/* ★可见的等待动效(2026-08-03 用户实测「没有动效」):fx-slot-wait 的透明度脉动
          在白底虚线框上肉眼不可见。加一道横扫高光 —— 所有 waiting 槽同相位(不带 delay),
          仍读作「这一片还没回来」,不指名哪张在画;待续出槽不动(它不在出)。 */}
      {slot.state === "waiting" && !slot.deferred && (
        <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <span className="fx-progress-sweep absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-foreground/[0.07] to-transparent" />
        </span>
      )}

      {slot.state === "failed" && (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 text-center text-destructive">
          <AlertCircle className="h-4 w-4" />
          {!dense && <span className="text-2xs leading-tight">没出来</span>}
        </span>
      )}
    </div>
  );
}
